import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import getIconSet from '@empiriecom/module-components/utils/getIconSet';
import Price from '@empiriecom/module-components/Price';
import { DimensionValue } from '@/interfaces';
import { Tile } from '../Tiles/Tile';
import { AvailabilityMessage } from '../../Delivery/AvailabilityMessage';

interface ListElementProps {
  variant: DimensionValue;
  onClick: () => void;
}

const ListElementWrapper = styled.div<{ isSelected: boolean; isOrderable: boolean }>`
  align-items: center;
  background: ${({ isSelected }) => (isSelected ? '#eee' : 'transparent')};
  border-bottom: 1px solid #eee;
  cursor: ${({ isOrderable }) => (isOrderable ? 'pointer' : 'auto')};
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  margin-bottom: 1px;
  &&:hover {
    background: #eee;
  }
`;

const TextElement = styled.div<{ fixedWidth: boolean }>`
  flex-grow: 2;
  ${({ fixedWidth }) => fixedWidth && 'width:30%;'}
  padding: 0 0.25rem;
  word-break: break-word;
  word-wrap: break-word;
`;

const AvailabilityWrapper = styled.div<{ mobile: boolean }>`
  ${({ mobile }) => mobile && 'svg { display: none; }'}
`;

const IconElement = styled.div`
  padding: 0 0.5rem;
`;

const PriceElement = styled.div<{ mobile: boolean }>`
  ${({ mobile }) => mobile && 'font-size: 0.75rem;'}
`;

export const ListElement: FunctionComponent<ListElementProps> = ({ onClick, variant }) => {
  const { rgb, image, isSelected, price, availability, displayText } = variant;
  const isAvailable = availability && availability.state !== 'NOT_AVAILABLE';
  const { ArrowMediumRight: ArrowRight, Checkmark } = getIconSet();
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  return (
    <ListElementWrapper
      data-testid="product-selection-select-element"
      data-isAvailable={isAvailable}
      isSelected={isSelected}
      isOrderable={availability ? isAvailable : true}
      onClick={() => (availability ? isAvailable && onClick() : onClick())}
    >
      {(rgb || image) && <Tile image={image} rgb={rgb} isInline={isMobile} />}
      <TextElement fixedWidth={!!(isMobile && availability && price)}>{displayText}</TextElement>
      {availability && (
        <AvailabilityWrapper mobile={isMobile}>
          <AvailabilityMessage state={availability.state} message={availability.message} />
        </AvailabilityWrapper>
      )}
      {price && (
        <PriceElement mobile={isMobile}>
          <Price currency={price.currency} price={price.value} />
        </PriceElement>
      )}
      <IconElement>
        {isSelected ? (
          <Checkmark size={isMobile ? '1.5rem' : '2rem'} />
        ) : (
          <ArrowRight size={isMobile ? '1.5rem' : '2rem'} />
        )}
      </IconElement>
    </ListElementWrapper>
  );
};

export default ListElement;
